import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TYPE_DATA_TABLE_CHART } from '@portal/common/models/chart-type';
import { CHART_SECTION, DATA_TABLE_SECTION } from '@portal/common/models/data-explorer-section';
import { isPdfExportEnabled } from '@portal/common/models/organization';
import { DataExplorerSection } from '@portal/common/types';

import { downloadPdfExportAsync } from '../../../store/data-explorer/actions';
import {
    getDataRecordsCount,
    getDataResponsesByType,
    getDataToolConfig,
    getSelectedChartType,
    getSelectedConditionsDataTypes,
} from '../../../store/data-explorer/selectors';
import { getOrganization } from '../../../store/organization/selectors';
import PdfReportModal from '../../DataExplorer/PdfReport/PdfReportModal';
import ExportPDFButton from '../../ExportPDFButton';

interface ExportPFRProps {
    areChartsHidden: boolean;
}

const ExportPDF: FC<ExportPFRProps> = ({ areChartsHidden }) => {
    const dispatch = useDispatch();

    const organization = useSelector(getOrganization);
    const dataResponsesByType = useSelector(getDataResponsesByType);
    const dataRecordsCount = useSelector(getDataRecordsCount);
    const selectedConditionsDataTypes = useSelector(getSelectedConditionsDataTypes);
    const dataToolConfig = useSelector(getDataToolConfig);
    const selectedChartType = useSelector(getSelectedChartType);

    const [isModalVisible, setIsModalVisible] = useState(false);

    const checkIsSectionDisabled = useCallback(
        (section: DataExplorerSection) => section === CHART_SECTION && areChartsHidden,
        [areChartsHidden]
    );

    const handlePdfReportModalOpen = useCallback(() => {
        setIsModalVisible(true);
    }, []);

    const handlePdfReportModalClose = useCallback(() => {
        setIsModalVisible(false);
    }, []);

    return (
        <>
            <ExportPDFButton
                disabled={!(dataResponsesByType && isPdfExportEnabled(organization))}
                onClick={handlePdfReportModalOpen}
                dataRecordsCount={dataRecordsCount}
            />
            {selectedConditionsDataTypes && dataToolConfig && (
                <>
                    <PdfReportModal
                        isVisible={isModalVisible}
                        onClose={handlePdfReportModalClose}
                        chartType={selectedChartType}
                        checkIsSectionCheckboxDisabled={checkIsSectionDisabled}
                        onSubmit={({ sectionsVisibility }) => {
                            const { sections } = dataToolConfig;
                            const chart_types = [selectedChartType];
                            if (sections.includes(DATA_TABLE_SECTION)) {
                                chart_types.push(TYPE_DATA_TABLE_CHART);
                            }
                            const pdf_report_options = Object.entries(sectionsVisibility).reduce(
                                (acc, [key, value]) => {
                                    if (
                                        value &&
                                        !checkIsSectionDisabled(key as DataExplorerSection)
                                    ) {
                                        acc.push(key);
                                    }

                                    return acc;
                                },
                                []
                            );

                            dispatch(
                                downloadPdfExportAsync.request({
                                    chart_types,
                                    pdf_report_options,
                                })
                            );
                        }}
                    />
                </>
            )}
        </>
    );
};

export default ExportPDF;
